import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { challengesAPI } from "../services/api";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, CheckCircle2, ChevronDown, ChevronUp,
  Waves, RotateCcw, Moon, Flame, Trophy
} from "lucide-react";
import { musicVolume, getStoredAudioMode } from "../utils/modeAudio";

/*
 * RECLAIM OUR PASSION — Daily Ritual Flow
 * Sacral chakra. Organic warmth. Living environment.
 */

// ─── Arrival ─────────────────────────────────────
const ArrivalMoment = () => (
  <motion.div className="min-h-[45vh] flex flex-col items-center justify-center px-8 relative"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 3 }}
    data-testid="challenge-arrival-text"
  >
    {/* Warm ember glow */}
    <motion.div className="absolute w-[350px] h-[200px] rounded-full pointer-events-none"
      style={{ background: 'radial-gradient(ellipse, rgba(195,90,30,0.06) 0%, transparent 60%)', filter: 'blur(40px)' }}
      animate={{ opacity: [0.4, 0.7, 0.4], scale: [1, 1.03, 1] }}
      transition={{ duration: 8, repeat: Infinity }}
    />
    <motion.p className="text-base text-center leading-loose relative z-10"
      style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic', color: 'rgba(0,0,0,0)' }}
      animate={{ color: 'rgba(216,191,163,0.4)' }}
      transition={{ delay: 0.5, duration: 3.5 }}
    >Take a breath… arrive…</motion.p>
  </motion.div>
);

// ─── Today's Practice ────────────────────────────
const TodaysPractice = ({ step }) => (
  <motion.div className="px-6 py-10"
    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.3, duration: 1 }}
    data-testid="challenge-practice-content"
  >
    <p className="text-[10px] uppercase tracking-[0.25em] mb-5" style={{ color: 'rgba(230,181,102,0.45)' }}>Today's Practice</p>
    <h3 className="text-lg mb-5" style={{ fontFamily: "'Playfair Display', serif", color: '#F5E9DCcc' }}>
      {step.title}
    </h3>
    <p className="text-sm leading-[1.8]" style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.45)' }}>
      {step.description}
    </p>
  </motion.div>
);

// ─── Optional Deepening ──────────────────────────
const OptionalDeepening = ({ navigate }) => (
  <motion.div className="px-6 py-8"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    transition={{ delay: 0.6, duration: 1 }}
    data-testid="challenge-deepening-links"
  >
    <p className="text-[10px] uppercase tracking-[0.2em] mb-5" style={{ color: 'rgba(216,191,163,0.22)' }}>Go Deeper</p>
    <div className="flex flex-wrap gap-2.5">
      {[
        { icon: Waves, label: 'Go deeper with immersion', path: '/abundance/living-vision-board' },
        { icon: RotateCcw, label: 'Use a Reset', path: '/healing' },
        { icon: Moon, label: 'Install this tonight', path: '/private-suite' },
      ].map((item) => (
        <button key={item.label} onClick={() => navigate(item.path)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full text-[11px] transition-all duration-500 hover:border-opacity-40"
          style={{ border: '1px solid rgba(195,90,30,0.12)', color: 'rgba(216,191,163,0.4)' }}
        >
          <item.icon size={12} />
          {item.label}
        </button>
      ))}
    </div>
  </motion.div>
);

// ─── Reflection ──────────────────────────────────
const ReflectionSection = ({ prompt, value, onChange }) => (
  <motion.div className="px-6 py-10"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    transition={{ delay: 0.8, duration: 1 }}
    data-testid="challenge-reflection-input"
  >
    {prompt && (
      <p className="text-sm mb-5 italic" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(245,233,220,0.45)' }}>
        {prompt}
      </p>
    )}
    <textarea value={value} onChange={(e) => onChange(e.target.value)}
      placeholder="Allow your reflection to emerge…"
      className="w-full bg-transparent text-sm leading-[1.8] resize-none outline-none"
      style={{
        fontFamily: "'Spectral', serif", fontStyle: 'italic',
        color: 'rgba(216,191,163,0.5)',
        borderBottom: '1px solid rgba(195,90,30,0.12)',
        paddingBottom: '16px', minHeight: '90px',
        caretColor: '#F2A65A',
      }}
    />
  </motion.div>
);

// ─── Complete Button ─────────────────────────────
const CompleteButton = ({ onClick, completing }) => (
  <motion.div className="px-6 py-10"
    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    transition={{ delay: 1, duration: 1 }}
  >
    <button onClick={onClick} disabled={completing}
      className="w-full py-4 rounded-full text-sm tracking-wide transition-all duration-700"
      style={{
        background: completing
          ? 'rgba(195,90,30,0.08)'
          : 'linear-gradient(135deg, #C35A1E, #E07A2F)',
        color: completing ? 'rgba(216,191,163,0.3)' : '#1A0C06',
        boxShadow: completing ? 'none' : '0 4px 30px rgba(195,90,30,0.25), 0 0 60px rgba(195,90,30,0.08)',
        fontFamily: "'Playfair Display', serif",
      }}
      data-testid="challenge-complete-btn"
    >{completing ? 'Completing…' : 'Complete Today'}</button>
  </motion.div>
);

// ─── Day Dots ────────────────────────────────────
const DayDots = ({ totalDays, completedDays, currentDay }) => (
  <div className="flex items-center justify-center gap-2 py-6">
    {Array.from({ length: totalDays }).map((_, i) => {
      const day = i + 1;
      const done = completedDays.includes(day);
      const isCurrent = day === currentDay;
      return (
        <motion.div key={day} className="rounded-full transition-all duration-500"
          style={{
            width: isCurrent ? 10 : 5, height: isCurrent ? 10 : 5,
            background: done ? 'rgba(242,166,90,0.6)' : isCurrent ? 'rgba(195,90,30,0.5)' : 'rgba(195,90,30,0.08)',
            border: isCurrent ? '1px solid rgba(224,122,47,0.5)' : 'none',
            boxShadow: isCurrent ? '0 0 8px rgba(195,90,30,0.2)' : 'none',
          }}
          animate={isCurrent ? { scale: [1, 1.15, 1] } : {}}
          transition={{ duration: 3, repeat: Infinity }}
        />
      );
    })}
  </div>
);

// ─── Hero's Journey Audio Companion ──────────────
// Soft ambient bed that plays during the active practice — uses the unified system.
const HeroJourneyAudio = () => {
  const [config, setConfig] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const mode = (typeof window !== 'undefined' && window.localStorage.getItem('reclaim_audio_mode')) || 'soft';
    fetch(`${API_URL}/api/unified-audio/resolve?area=challenges_hero_journey&mode=${mode}`)
      .then(r => r.json()).then(setConfig).catch(() => {});
  }, []);

  const togglePlay = async () => {
    if (!audioRef.current && config?.music?.url) {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const url = config.music.url.startsWith('http') ? config.music.url : `${API_URL}${config.music.url}`;
      audioRef.current = new Audio(url);
      audioRef.current.loop = true;
      // Slightly lifted above the pure music baseline so the companion
      // still feels present even in "clear" mode — but capped at 0.45.
      audioRef.current.volume = Math.min(0.45, musicVolume(getStoredAudioMode()) + 0.15);
    }
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      try { await audioRef.current.play(); setPlaying(true); } catch {}
    }
  };

  // Cleanup on unmount
  useEffect(() => () => { try { audioRef.current?.pause(); } catch {} }, []);

  if (!config?.music?.url) return null;
  return (
    <div className="px-6 py-4 flex items-center justify-center" data-testid="hero-journey-audio">
      <button
        onClick={togglePlay}
        className="flex items-center gap-2 px-4 py-2 rounded-full transition-all"
        style={{
          background: playing ? 'rgba(230,181,102,0.12)' : 'rgba(245,233,220,0.04)',
          border: `1px solid ${playing ? 'rgba(230,181,102,0.35)' : 'rgba(245,233,220,0.1)'}`,
        }}
        data-testid="hero-journey-audio-toggle"
      >
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ background: playing ? '#E6B566' : 'rgba(245,233,220,0.3)' }}
        />
        <span
          className="text-[10px] uppercase tracking-[0.25em] font-light"
          style={{ color: playing ? '#E6B566' : 'rgba(245,233,220,0.4)' }}
        >
          {playing ? 'Companion playing' : `Play companion · ${config.music.name}`}
        </span>
      </button>
    </div>
  );
};

// ─── Past Day Card ───────────────────────────────
const PastDayCard = ({ step, reflection, isExpanded, onToggle }) => (
  <button onClick={onToggle} className="w-full text-left">
    <div className="px-6 py-3.5 flex items-center gap-3" style={{ borderBottom: '1px solid rgba(195,90,30,0.05)' }}>
      <CheckCircle2 size={13} style={{ color: 'rgba(242,166,90,0.35)' }} className="flex-shrink-0" />
      <span className="text-[11px] flex-1" style={{ color: 'rgba(216,191,163,0.3)' }}>Day {step.day}: {step.title}</span>
      {isExpanded ? <ChevronUp size={11} style={{ color: 'rgba(216,191,163,0.15)' }} /> : <ChevronDown size={11} style={{ color: 'rgba(216,191,163,0.15)' }} />}
    </div>
    <AnimatePresence>
      {isExpanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
          <div className="px-6 py-3 pl-12">
            <p className="text-[11px] leading-relaxed mb-2" style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.3)' }}>{step.description}</p>
            {reflection && (
              <div className="mt-2 pl-3" style={{ borderLeft: '1px solid rgba(195,90,30,0.15)' }}>
                <p className="text-[11px] italic" style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.4)' }}>{reflection}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  </button>
);

// ═══════════════════════════════════════════════════
// Main
// ═══════════════════════════════════════════════════
export const ChallengeDetailPage = () => {
  const navigate = useNavigate();
  const { challengeId } = useParams();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [reflectionText, setReflectionText] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [expandedPastDay, setExpandedPastDay] = useState(null);

  useEffect(() => { fetchChallenge(); }, [challengeId]);

  const fetchChallenge = async () => {
    try { const res = await challengesAPI.getById(challengeId); setChallenge(res.data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleStart = async () => {
    setStarting(true);
    try { await challengesAPI.start(challengeId); await fetchChallenge(); }
    catch (e) { console.error(e); }
    finally { setStarting(false); }
  };

  const handleCompleteDay = async () => {
    if (!challenge?.current_day) return;
    setCompleting(true);
    try {
      const res = await challengesAPI.completeDay(challengeId, challenge.current_day, reflectionText || null);
      if (res.data.is_challenge_complete) setShowSuccess(true);
      setReflectionText("");
      await fetchChallenge();
    } catch (e) { console.error(e); }
    finally { setCompleting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1A0C06' }}>
        <motion.div className="w-8 h-8 rounded-full" style={{ border: '1px solid rgba(195,90,30,0.2)' }}
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#1A0C06' }}>
        <div className="text-center">
          <p className="text-sm mb-4" style={{ color: 'rgba(216,191,163,0.3)' }}>Challenge not found</p>
          <button onClick={() => navigate("/challenges")} className="text-sm" style={{ color: 'rgba(242,166,90,0.5)' }}>Back</button>
        </div>
      </div>
    );
  }

  const progress = challenge.user_progress;
  const isStarted = progress && progress.status === "in_progress";
  const isCompleted = progress && progress.status === "completed";
  const completedDays = progress?.completed_days || [];
  const dayReflections = progress?.day_reflections || {};
  const currentDay = challenge.current_day || 1;
  const currentStep = challenge.daily_steps?.find(s => s.day === currentDay);
  const pastSteps = challenge.daily_steps?.filter(s => completedDays.includes(s.day)) || [];

  return (
    <div className="min-h-screen pb-24" style={{ background: '#1A0C06' }} data-testid="challenge-detail-page">
      {/* Living background */}
      <div className="fixed inset-0 pointer-events-none" style={{
        background: `
          radial-gradient(ellipse at 40% 20%, rgba(195,90,30,0.07) 0%, transparent 50%),
          radial-gradient(ellipse at 60% 70%, rgba(195,90,30,0.04) 0%, transparent 45%)
        `,
      }} />
      <motion.div className="fixed pointer-events-none"
        style={{
          width: '100%', height: '35%', top: '5%',
          background: 'radial-gradient(ellipse at 50% 50%, rgba(195,90,30,0.06) 0%, transparent 60%)',
          filter: 'blur(30px)',
        }}
        animate={{ opacity: [0.4, 0.7, 0.4] }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <div className="fixed inset-0 pointer-events-none opacity-[0.04]" style={{
        backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 512 512\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.75\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        backgroundSize: '256px 256px',
      }} />
      <div className="fixed inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(12,6,2,0.4) 100%)',
      }} />

      {/* Back */}
      <div className="relative z-20 px-6 pt-8">
        <button onClick={() => navigate("/challenges")} className="transition-opacity hover:opacity-70"
          style={{ color: 'rgba(216,191,163,0.25)' }} data-testid="challenge-back-btn"
        ><ArrowLeft size={20} /></button>
      </div>

      <div className="relative z-10 max-w-lg mx-auto">
        {/* Title */}
        <motion.div className="px-6 pt-6 pb-3"
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.5 }}
        >
          <p className="text-[10px] uppercase tracking-[0.25em] mb-3" style={{ color: 'rgba(230,181,102,0.4)' }}>
            {challenge.category?.replace(/_/g, " ")}
          </p>
          <h1 className="text-xl mb-3" style={{ fontFamily: "'Playfair Display', serif", color: '#F5E9DCcc' }}>
            {challenge.title}
          </h1>
          <p className="text-xs italic leading-relaxed" style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.32)' }}>
            {challenge.description}
          </p>
        </motion.div>

        {isStarted && <DayDots totalDays={challenge.duration} completedDays={completedDays} currentDay={currentDay} />}

        {/* Not Started */}
        {!isStarted && !isCompleted && (
          <motion.div className="px-6 py-20 text-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 1 }}
          >
            <p className="text-sm italic mb-10" style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.35)' }}>
              {challenge.duration} days of guided practice
            </p>
            <button onClick={handleStart} disabled={starting}
              className="px-10 py-3.5 rounded-full text-sm tracking-wide transition-all duration-700"
              style={{
                background: starting ? 'rgba(195,90,30,0.08)' : 'linear-gradient(135deg, #C35A1E, #E07A2F)',
                color: starting ? 'rgba(216,191,163,0.3)' : '#1A0C06',
                boxShadow: starting ? 'none' : '0 4px 30px rgba(195,90,30,0.2)',
                fontFamily: "'Playfair Display', serif",
              }}
              data-testid="start-challenge-btn"
            >{starting ? 'Beginning…' : 'Begin This Challenge'}</button>
          </motion.div>
        )}

        {/* Active: Daily Ritual */}
        {isStarted && currentStep && (
          <>
            <ArrivalMoment />
            <HeroJourneyAudio />
            <TodaysPractice step={currentStep} />
            <OptionalDeepening navigate={navigate} />
            {currentStep.reflection_prompt && (
              <ReflectionSection prompt={currentStep.reflection_prompt} value={reflectionText} onChange={setReflectionText} />
            )}
            <CompleteButton onClick={handleCompleteDay} completing={completing} />
          </>
        )}

        {/* Completed */}
        {isCompleted && (
          <motion.div className="px-6 py-20 text-center"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.5 }}
          >
            <motion.div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'rgba(195,90,30,0.08)', border: '1px solid rgba(195,90,30,0.2)', boxShadow: '0 0 30px rgba(195,90,30,0.06)' }}
            >
              <Trophy size={24} style={{ color: 'rgba(242,166,90,0.45)' }} />
            </motion.div>
            <p className="text-sm" style={{ fontFamily: "'Playfair Display', serif", color: 'rgba(245,233,220,0.5)' }}>Challenge Complete</p>
            <p className="text-xs italic mt-2" style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.3)' }}>You've completed this journey</p>
          </motion.div>
        )}

        {/* Past Days */}
        {pastSteps.length > 0 && (
          <div className="mt-10 mb-4">
            <p className="px-6 text-[9px] uppercase tracking-[0.2em] mb-3" style={{ color: 'rgba(216,191,163,0.15)' }}>Your Journey</p>
            {pastSteps.map(step => (
              <PastDayCard key={step.day} step={step}
                reflection={dayReflections[String(step.day)]}
                isExpanded={expandedPastDay === step.day}
                onToggle={() => setExpandedPastDay(expandedPastDay === step.day ? null : step.day)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-8"
            style={{ background: 'rgba(18,8,4,0.95)' }}
            onClick={() => setShowSuccess(false)}
          >
            <motion.div className="absolute w-[400px] h-[400px] rounded-full pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(195,90,30,0.08) 0%, transparent 60%)', filter: 'blur(50px)' }}
              animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 5, repeat: Infinity }}
            />
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
              className="text-center relative z-10" onClick={(e) => e.stopPropagation()}
            >
              <motion.div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                style={{ background: 'rgba(195,90,30,0.1)', border: '1px solid rgba(224,122,47,0.25)', boxShadow: '0 0 40px rgba(195,90,30,0.1)' }}
              >
                <Trophy size={30} style={{ color: 'rgba(242,166,90,0.5)' }} />
              </motion.div>
              <h2 className="text-xl mb-3" style={{ fontFamily: "'Playfair Display', serif", color: '#F5E9DC99' }}>Challenge Complete</h2>
              <p className="text-sm italic mb-10" style={{ fontFamily: "'Spectral', serif", color: 'rgba(216,191,163,0.4)' }}>Your dedication is inspiring</p>
              <button onClick={() => navigate("/challenges")}
                className="px-8 py-3 rounded-full text-sm transition-all duration-300"
                style={{ border: '1px solid rgba(195,90,30,0.15)', color: 'rgba(216,191,163,0.5)' }}
              >More Challenges</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ChallengeDetailPage;
